export type AnalyticsRequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export interface AnalyticsUser {
  id: string;
  firmId: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  createdAt?: Date;
}

export interface AnalyticsFirm {
  id: string;
  name: string;
  createdAt: Date;
}

export type AnalyticsPropertyValue =
  | boolean
  | number
  | string
  | null
  | readonly boolean[]
  | readonly number[]
  | readonly string[];

export const ANALYTICS_EVENT_PROPERTY_CONTRACTS = {
  advisor_page_viewed: {
    page_name: [
      'dashboard',
      'clients',
      'client_detail',
      'assessment',
      'full_screen_scorecard',
      'one_page_plan',
      'messages',
      'integrations',
      'elements_labs',
      'roadmaps',
      'customize_elements',
      'ai_connections',
      'reports',
      'invitations',
      'settings',
    ],
    feature: [
      'dashboard',
      'clients',
      'assessment',
      'planning',
      'messaging',
      'integrations',
      'labs',
      'roadmaps',
      'customization',
      'ai_connections',
      'reports',
      'invitations',
      'settings',
    ],
  },
  advisor_search_opened: {
    surface: ['dashboard'],
  },
  advisor_search_result_selected: {
    result_type: ['client', 'destination'],
    input_state: ['empty', 'query'],
  },
  advisor_firm_switched: {
    source: ['firm_selector'],
  },
  advisor_feature_setting_updated: {
    feature: ['estate_status', 'customize_elements', 'ai_connections', 'progress_reports'],
    state: ['enabled', 'disabled'],
  },
  advisor_roadmap_opened: {
    feature: ['estate_status', 'customize_elements'],
  },
  advisor_customize_elements_saved: {
    scope: ['firm'],
  },
  advisor_ai_connection_url_copied: {
    provider: ['remote_mcp'],
  },
  advisor_mcp_consent_resolved: {
    decision: ['allowed', 'denied'],
  },
  advisor_report_downloaded: {
    report_type: [
      'burn_rate',
      'business_term',
      'debt_rate',
      'estate_status',
      'equity_rate',
      'insurance_rate',
      'liquid_term',
      'one_page_plan',
      'personal_financial_statement',
      'progress_report',
      'qualified_term',
      'real_estate_term',
      'savings_rate',
      'tax_rate',
      'total_term',
    ],
  },
  advisor_invitation_batch_completed: {
    result: ['succeeded', 'failed'],
    onboarding_mode: ['self_directed', 'advisor_directed'],
  },
  client_message_sent: {
    surface: ['dashboard', 'embedded'],
    conversation_state: ['started', 'existing'],
  },
  client_account_mutation_completed: {
    action: ['closed', 'deleted'],
  },
  client_mfa_setting_updated: {
    action: ['enabled', 'disabled'],
  },
  client_mfa_setting_update_failed: {
    action: ['enabled', 'disabled'],
    reason: ['request_failed'],
  },
  client_page_viewed: {
    page_name: [
      'dashboard',
      'assessment',
      'full_screen_scorecard',
      'estate_status',
      'one_page_plan',
      'next_steps',
      'messages',
      'accounts',
      'closed_accounts',
      'settings',
      'mfa',
      'onboarding',
      'calendar',
      'team',
    ],
    feature: [
      'dashboard',
      'assessment',
      'estate_status',
      'planning',
      'messaging',
      'accounts',
      'authentication',
      'settings',
      'onboarding',
      'calendar',
      'team',
    ],
  },
} as const;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENT_PROPERTY_CONTRACTS;

type AnalyticsContractValue<Value> = Value extends readonly (infer Item)[] ? Item : never;

export type AnalyticsEventMap = {
  [EventName in AnalyticsEventName]: {
    [PropertyName in keyof typeof ANALYTICS_EVENT_PROPERTY_CONTRACTS[EventName]]: AnalyticsContractValue<
      typeof ANALYTICS_EVENT_PROPERTY_CONTRACTS[EventName][PropertyName]
    >;
  };
};

export const ANALYTICS_EVENT_NAMES = Object.keys(ANALYTICS_EVENT_PROPERTY_CONTRACTS) as AnalyticsEventName[];

export type AnalyticsEventInput<EventName extends AnalyticsEventName> = {
  eventName: EventName;
  params: AnalyticsEventMap[EventName];
};

export type AnyAnalyticsEventInput = {
  [EventName in AnalyticsEventName]: AnalyticsEventInput<EventName>;
}[AnalyticsEventName];
